import React, { useState, useEffect, useRef } from "react";
import { observer } from "mobx-react";
import { pullContactState } from "./store";
import customListState from "../Dashboard/dataSearch/CustomList/state";
import Utils from "Utils/utils";

const AutoCompleteHeader = observer(props => {
  const {
    optionList = [],
    showDropdown = false,
    setShowDropdown = () => {},
    setUpdatedOptions = () => {},
    searchValue = "",
    setSearchValue = () => {},
    isCreateButtonClicked = false,
    setIsCreateButtonClicked = () => {}
  } = props || {};
  const dropdownRef = useRef(null);

  const filterBySearchText = (value = "") => {
    let filteredData =
      optionList?.length > 0 &&
      optionList?.filter(item => {
        return item?.name?.toString()?.toLowerCase()?.includes(value);
      });
    setUpdatedOptions(filteredData);
  };

  const handleInputChange = event => {
    pullContactState.setCreateListError("");
    const value = event?.currentTarget?.value || "";
    const updatedValue = value;
    setSearchValue(updatedValue);
    const lowercasedValue = updatedValue?.toLowerCase();
    if (lowercasedValue === "") {
      setUpdatedOptions(optionList);
    } else {
      filterBySearchText(lowercasedValue);
    }
  };

  const clearSearchInput = event => {
    event.preventDefault();
    event.stopPropagation();
    setSearchValue("");
    setUpdatedOptions(optionList);
    focusInputBox();
    pullContactState.setCreateListError("");
  };

  const scrollIntoDropdownView = () => {
    setTimeout(() => {
      dropdownRef &&
        dropdownRef.current &&
        dropdownRef.current.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "start"
        });
    }, 150);
  };

  const handleHeaderClick = () => {
    if (!showDropdown) {
      setShowDropdown(true);
      scrollIntoDropdownView();
    }
  };

  const focusInputBox = () => {
    dropdownRef && dropdownRef.current && dropdownRef.current.focus();
  };

  const handleBlur = async event => {
    if (isCreateButtonClicked) {
      // Reset flag and do nothing on blur
      setIsCreateButtonClicked(false);
      return;
    }
    if (!searchValue) {
      return;
    }
    const listExists = optionList.some(
      item => item.name.toLowerCase() === searchValue.toLowerCase()
    );
    if (listExists) {
      pullContactState.setCreateListError("List name already exists");
      return;
    }
    await pullContactState.createContactList(searchValue);
  };

  return (
    <div className="dropdownHeader" onClick={handleHeaderClick}>
      <div className="dropdownInputHeader" onBlur={handleBlur}>
        <input
          className="dropdownTitle"
          type="text"
          placeholder="Select or Create list"
          onChange={handleInputChange}
          value={searchValue}
          ref={dropdownRef}
        />
        {searchValue?.length > 0 && (
          <i
            className="material-icons clearIpIcon"
            onClick={clearSearchInput}
            onMouseDown={e => e.preventDefault()}
          >
            close
          </i>
        )}
        <i
          className={`material-icons arrowIcon ${showDropdown ? "rotate" : ""}`}
        >
          keyboard_arrow_down
        </i>
      </div>
    </div>
  );
});

const CreateNewList = observer(props => {
  const {
    setShowDropdown = () => {},
    searchValue = "",
    setIsCreateButtonClicked = () => {}
  } = props || {};

  const handleCreateNewList = async () => {
    setShowDropdown(false);
    await pullContactState.createContactList(searchValue);
    setIsCreateButtonClicked(false);
  };

  return (
    <div
      className="createNewListBtn"
      onClick={handleCreateNewList}
      onMouseDown={() => setIsCreateButtonClicked(true)}
    >
      <span className="btnText">{`Create new list "${searchValue}"`}</span>
    </div>
  );
});

const AutoCompleteBody = observer(props => {
  const {
    searchValue = "",
    showDropdown = false,
    updatedOptions = [],
    dropDownValue = "",
    cbk = () => {},
    setShowDropdown = () => {},
    setSearchValue = () => {},
    setIsCreateButtonClicked = () => {}
  } = props || {};

  const handleDropdownChange = (item, index) => {
    cbk(item, index);
    setSearchValue(item?.name);
    setShowDropdown(false);
    pullContactState.setDisableExtractBtn(false);
  };

  return (
    <>
      {searchValue.trim() && (
        <li className="dropdownOptions hasCreateList">
          <span className={`updatedName`}>
            <CreateNewList
              showDropdown={showDropdown}
              setShowDropdown={setShowDropdown}
              searchValue={searchValue}
              setSearchValue={setSearchValue}
              setIsCreateButtonClicked={setIsCreateButtonClicked}
            />
          </span>
        </li>
      )}
      {updatedOptions.length > 0 ? (
        updatedOptions?.map((item, index) => (
          <li
            key={`limit${item?.name}`}
            className={`dropdownOptions ${
              dropDownValue === item?.name && !item?.disabled ? "active" : ""
            } ${item?.disabled ? "disabled" : ""}`}
            onClick={
              item?.disabled ? () => {} : e => handleDropdownChange(item, index)
            }
          >
            <>
              <span
                className={`updatedName ${
                  item?.id?.toLowerCase() === "loading" ? "loading" : ""
                } `}
              >
                {item?.name}
              </span>
            </>
          </li>
        ))
      ) : (
        <li className="dropdownOptions noResultsFound">No results found</li>
      )}
    </>
  );
});

const CustomAutoCompleteDropdown = observer(props => {
  const {
    cbk = () => {},
    optionList = [],
    defaultValue = "",
    disabled = false
  } = props || {};

  const [showDropdown, setShowDropdown] = useState(false);
  const [updatedOptions, setUpdatedOptions] = useState([]);
  const [dropDownValue, setDropdownValue] = useState(defaultValue);
  const [searchValue, setSearchValue] = useState("");
  const [isCreateButtonClicked, setIsCreateButtonClicked] = useState(false);

  useEffect(() => {
    optionList && optionList?.length > 0 && setUpdatedOptions(optionList);
  }, [optionList]);

  return (
    <>
      <div
        className={`customAutoCompleteDropdownWrapper ${
          disabled ? "disableDropdown" : ""
        }`}
      >
        <AutoCompleteHeader
          optionList={optionList}
          showDropdown={showDropdown}
          setShowDropdown={setShowDropdown}
          setUpdatedOptions={setUpdatedOptions}
          searchValue={searchValue}
          setSearchValue={setSearchValue}
          isCreateButtonClicked={isCreateButtonClicked}
          setIsCreateButtonClicked={setIsCreateButtonClicked}
        />
        {showDropdown && (
          <div className="dropdownPanel">
            <ul className="dropdownList">
              <AutoCompleteBody
                optionList={optionList}
                updatedOptions={updatedOptions}
                setUpdatedOptions={setUpdatedOptions}
                dropDownValue={dropDownValue}
                setDropdownValue={setDropdownValue}
                setShowDropdown={setShowDropdown}
                cbk={cbk}
                searchValue={searchValue}
                setSearchValue={setSearchValue}
                setIsCreateButtonClicked={setIsCreateButtonClicked}
              />
            </ul>
          </div>
        )}
      </div>
      {showDropdown && (
        <div
          className="autoCompleteDropdownOverlay"
          onClick={() => {
            setShowDropdown(false);
          }}
        />
      )}
    </>
  );
});

export { CustomAutoCompleteDropdown };
export default CustomAutoCompleteDropdown;
